
import React, { useState, useEffect } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { useNavigate } from "react-router-dom";
import "./UploadFotoFuncionario.css";

const API = "https://acesso.cronosagenda.com.br";

/**
 * Normaliza URLs antigas que podem conter:
 * /uploads//uploads/arquivo.webp
 * /uploads/uploads/arquivo.webp
 * uploads/arquivo.webp
 *
 * Mantém URLs data: e blob: usadas na pré-visualização.
 */
const normalizarFotoUrl = (url) => {
  if (!url || typeof url !== "string") return "";

  const caminho = url.trim();

  if (/^(data:|blob:)/i.test(caminho)) {
    return caminho;
  }

  try {
    const urlCompleta = /^https?:\/\//i.test(caminho)
      ? new URL(caminho)
      : new URL(caminho.startsWith("/") ? caminho : `/${caminho}`, API);

    // Para fotos hospedadas no backend, normaliza o caminho.
    if (urlCompleta.origin === new URL(API).origin) {
      const segmentos = urlCompleta.pathname
        .replace(/\\/g, "/")
        .split("/")
        .filter(Boolean);

      // Remove qualquer prefixo "uploads" repetido.
      while (
        segmentos.length > 0 &&
        segmentos[0].toLowerCase() === "uploads"
      ) {
        segmentos.shift();
      }

      if (segmentos.length === 0) return "";

      urlCompleta.pathname = `/uploads/${segmentos.join("/")}`;
      return urlCompleta.toString();
    }

    // Imagens hospedadas em outros domínios continuam funcionando.
    return urlCompleta.toString();
  } catch (error) {
    console.error("URL de foto inválida:", caminho, error);
    return "";
  }
};

const UploadFotoFuncionario = () => {
  const [funcionarios, setFuncionarios] = useState([]);
  const [funcionarioId, setFuncionarioId] = useState("");
  const [imagem, setImagem] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mensagem, setMensagem] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [processandoImagem, setProcessandoImagem] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.get(`${API}/api/verificar-autenticacao`, {
          withCredentials: true,
        });

        const res = await axios.get(`${API}/api/funcionarios`, {
          withCredentials: true,
        });

        setFuncionarios(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("Erro ao carregar funcionários:", error);
        navigate("/login");
      }
    };

    fetchData();
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMensagem("Selecione um arquivo de imagem válido.");
      e.target.value = "";
      return;
    }

    setMensagem("");
    setProcessandoImagem(true);

    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.2,
        maxWidthOrHeight: 300,
        useWebWorker: true,
      });

      setImagem(compressed);
      setPreview(URL.createObjectURL(compressed));
    } catch (error) {
      console.error("Erro ao processar imagem:", error);
      setMensagem("Erro ao processar imagem. Tente outra foto.");
      setImagem(null);
      setPreview(null);
    } finally {
      setProcessandoImagem(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!funcionarioId || !imagem) {
      setMensagem("Selecione um funcionário e uma imagem.");
      return;
    }

    setCarregando(true);
    setMensagem("");

    try {
      const formData = new FormData();
      formData.append("foto", imagem);
      formData.append("funcionario_id", funcionarioId);

      const res = await axios.post(
        `${API}/api/upload-funcionario`,
        formData,
        { withCredentials: true }
      );

      const novaUrl = normalizarFotoUrl(res.data?.url);

      if (!novaUrl) {
        throw new Error("O servidor não retornou uma URL de foto válida.");
      }

      setFuncionarios((prev) =>
        prev.map((f) =>
          String(f.id) === String(funcionarioId)
            ? { ...f, foto_url: novaUrl }
            : f
        )
      );

      setMensagem("Foto atualizada com sucesso!");

      setImagem(null);
      setPreview(null);
      setFuncionarioId("");

      const inputArquivo = document.getElementById(
        "upload-foto-arquivo"
      );

      if (inputArquivo) {
        inputArquivo.value = "";
      }
    } catch (error) {
      console.error("Erro ao enviar foto:", error);
      setMensagem("Erro ao enviar imagem. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  const removerFoto = async (id) => {
    if (!window.confirm("Tem certeza de que deseja remover esta foto?")) {
      return;
    }

    setMensagem("");

    try {
      await axios.post(
        `${API}/api/remover-foto-funcionario`,
        { funcionario_id: id },
        { withCredentials: true }
      );

      setFuncionarios((prev) =>
        prev.map((f) =>
          String(f.id) === String(id)
            ? { ...f, foto_url: null }
            : f
        )
      );

      setMensagem("Foto removida com sucesso!");
    } catch (error) {
      console.error("Erro ao remover foto:", error);
      setMensagem("Erro ao remover foto. Tente novamente.");
    }
  };

  const funcionariosComFoto = funcionarios.filter(
    (f) => f.foto_url
  );

  const funcionarioSelecionado = funcionarios.find(
    (f) => String(f.id) === String(funcionarioId)
  );

  const fotoAtualUrl = normalizarFotoUrl(
    funcionarioSelecionado?.foto_url
  );

  return (
    <main className="upload-foto-page">
      <div className="upload-foto-container">
        <header className="upload-foto-header">
          <div
            className="upload-foto-header-icon"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="5" width="18" height="15" rx="3" />
              <circle cx="12" cy="12" r="3" />
              <path d="m7 5 1.5-2h7L17 5" />
            </svg>
          </div>

          <div className="upload-foto-header-content">
            <span className="upload-foto-eyebrow">
              EQUIPE E PERSONALIZAÇÃO
            </span>
            <h1>Fotos dos funcionários</h1>
            <p>
              Gerencie as fotos da sua equipe e deixe sua agenda com a
              identidade dos seus profissionais.
            </p>
          </div>
        </header>

        <section className="upload-foto-card">
          <div className="upload-foto-card-heading">
            <div>
              <h2>Atualizar foto</h2>
              <p>Escolha um profissional e envie uma imagem.</p>
            </div>

            <span className="upload-foto-step">01</span>
          </div>

          <form className="upload-foto-form" onSubmit={handleSubmit}>
            <div className="upload-foto-field">
              <label htmlFor="upload-foto-funcionario">
                Profissional
              </label>

              <select
                id="upload-foto-funcionario"
                value={funcionarioId}
                onChange={(e) => {
                  setFuncionarioId(e.target.value);
                  setMensagem("");
                }}
                required
              >
                <option value="">Selecione um funcionário</option>

                {funcionarios.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="upload-foto-preview-area">
              <div className="upload-foto-preview">
                {preview ? (
                  <img
                    className="upload-foto-person-image"
                    src={preview}
                    alt={`Pré-visualização da foto${
                      funcionarioSelecionado
                        ? ` de ${funcionarioSelecionado.nome}`
                        : ""
                    }`}
                  />
                ) : fotoAtualUrl ? (
                  <img
                    className="upload-foto-person-image"
                    src={fotoAtualUrl}
                    alt={`Foto de ${funcionarioSelecionado.nome}`}
                    onError={(e) => {
                      console.error("Erro ao carregar foto atual:", {
                        funcionario: funcionarioSelecionado?.nome,
                        valorOriginal: funcionarioSelecionado?.foto_url,
                        urlTentada: e.currentTarget.src,
                      });
                    }}
                  />
                ) : (
                  <div className="upload-foto-placeholder">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="8" r="4" />
                      <path d="M5 21a7 7 0 0 1 14 0" />
                      <path d="M19 4v4m-2-2h4" />
                    </svg>
                  </div>
                )}

                {preview && (
                  <span className="upload-foto-preview-badge">
                    Pré-visualização
                  </span>
                )}
              </div>

              <div className="upload-foto-preview-caption">
                <strong>
                  {preview
                    ? "Foto pronta para envio"
                    : funcionarioSelecionado
                    ? funcionarioSelecionado.nome
                    : "Sua foto aparecerá aqui"}
                </strong>

                <span>
                  {preview
                    ? "Confira a imagem antes de salvar."
                    : "Prefira uma foto nítida, com o rosto bem visível."}
                </span>
              </div>
            </div>

            <div className="upload-foto-file-area">
              <label
                className="upload-foto-file-label"
                htmlFor="upload-foto-arquivo"
              >
                <span
                  className="upload-foto-file-icon"
                  aria-hidden="true"
                >
                  ↑
                </span>

                <span className="upload-foto-file-text">
                  <strong>
                    {processandoImagem
                      ? "Processando imagem..."
                      : "Escolher imagem"}
                  </strong>
                  <small>
                    Formatos de imagem · Compressão automática
                  </small>
                </span>

                <span className="upload-foto-file-action">
                  Procurar
                </span>
              </label>

              <input
                id="upload-foto-arquivo"
                className="upload-foto-file-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={processandoImagem || carregando}
              />

              <p className="upload-foto-file-hint">
                A imagem será redimensionada e comprimida antes do envio.
              </p>
            </div>

            <button
              className="upload-foto-save-btn"
              type="submit"
              disabled={
                carregando ||
                processandoImagem ||
                !funcionarioId ||
                !imagem
              }
            >
              {carregando ? (
                <>
                  <span className="upload-foto-spinner" />
                  Enviando foto...
                </>
              ) : processandoImagem ? (
                "Processando imagem..."
              ) : (
                <>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
                    <path d="M17 21v-8H7v8M7 3v5h8" />
                  </svg>
                  Salvar foto
                </>
              )}
            </button>
          </form>

          {mensagem && (
            <div
              className={`upload-foto-message ${
                mensagem.toLowerCase().includes("erro") ||
                mensagem.toLowerCase().includes("selecione")
                  ? "upload-foto-message-error"
                  : "upload-foto-message-success"
              }`}
              role="status"
            >
              <span aria-hidden="true">
                {mensagem.toLowerCase().includes("erro") ||
                mensagem.toLowerCase().includes("selecione")
                  ? "!"
                  : "✓"}
              </span>
              {mensagem}
            </div>
          )}
        </section>

        <section className="upload-foto-list-section">
          <div className="upload-foto-list-heading">
            <div>
              <span className="upload-foto-eyebrow">
                GERENCIAMENTO DA EQUIPE
              </span>
              <h2>Fotos cadastradas</h2>
              <p>
                Visualize e remova as fotos dos profissionais.
              </p>
            </div>

            <div className="upload-foto-counter">
              <strong>{funcionariosComFoto.length}</strong>
              <span>
                {funcionariosComFoto.length === 1 ? "foto" : "fotos"}
              </span>
            </div>
          </div>

          {funcionariosComFoto.length === 0 ? (
            <div className="upload-foto-empty">
              <div
                className="upload-foto-empty-icon"
                aria-hidden="true"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="3" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="m21 15-5-5L5 21" />
                </svg>
              </div>

              <h3>Nenhuma foto cadastrada</h3>
              <p>
                Quando você adicionar fotos aos profissionais, elas
                aparecerão nesta seção.
              </p>
            </div>
          ) : (
            <div className="upload-foto-list">
              {funcionariosComFoto.map((f) => {
                const fotoUrl = normalizarFotoUrl(f.foto_url);

                return (
                  <article
                    className="upload-foto-person"
                    key={f.id}
                  >
                    <img
                      className="upload-foto-person-image"
                      src={fotoUrl}
                      alt={`Foto de ${f.nome}`}
                      loading="lazy"
                      onError={(e) => {
                        console.error(`Erro ao carregar foto de ${f.nome}:`, {
                          valorOriginal: f.foto_url,
                          urlTentada: e.currentTarget.src,
                        });
                      }}
                    />

                    <div className="upload-foto-person-info">
                      <strong>{f.nome}</strong>
                      <span>
                        <span className="upload-foto-status-dot" />
                        Foto cadastrada
                      </span>
                    </div>

                    <button
                      className="upload-foto-remove-btn"
                      type="button"
                      onClick={() => removerFoto(f.id)}
                      aria-label={`Remover foto de ${f.nome}`}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 6h18" />
                        <path d="M8 6V4h8v2" />
                        <path d="m19 6-1 14H6L5 6" />
                        <path d="M10 11v5m4-5v5" />
                      </svg>
                      <span>Remover foto</span>
                    </button>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <footer className="upload-foto-footer">
          <button
            className="upload-foto-back-btn"
            type="button"
            onClick={() => navigate("/dashboard")}
          >
            <span aria-hidden="true">←</span>
            Voltar ao painel
          </button>
        </footer>
      </div>
    </main>
  );
};

export default UploadFotoFuncionario;
